import React, { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { DayView } from './DayView';
import { AgendaView } from './AgendaView';
import type { CalendarEvent, CalendarViewMode } from './types';

export interface CalendarProps {
  initialDate?: Date;
  initialView?: CalendarViewMode;
  events?: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  className?: string;
  onRangeChange?: (from: Date, to: Date, view: CalendarViewMode) => void;
}

function startOfWeek(date: Date, firstDay: 0 | 1 = 1) {
  const d = new Date(date);
  const diff = (d.getDay() + 7 - firstDay) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(d: Date) {
  const e = new Date(d);
  e.setHours(23,59,59,999);
  return e;
}

function monthRange(date: Date) {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return { from, to };
}

function weekRange(date: Date) {
  const from = startOfWeek(date);
  const to = endOfDay(new Date(from.getTime() + 6 * 24 * 60 * 60 * 1000));
  return { from, to };
}

function dayRange(date: Date) {
  const from = new Date(date);
  from.setHours(0,0,0,0);
  const to = endOfDay(from);
  return { from, to };
}

export const Calendar: React.FC<CalendarProps> = ({ 
  initialDate = new Date(), 
  initialView = 'month', 
  events, 
  onEventClick,
  className,
  onRangeChange 
}) => {
  const [activeDate, setActiveDate] = useState<Date>(initialDate);
  const [view, setView] = useState<CalendarViewMode>(initialView);
  const rangeCbRef = useRef<CalendarProps['onRangeChange'] | null>(null);

  // Keep latest callback in a ref to avoid effect churn on identity changes
  useEffect(() => { rangeCbRef.current = onRangeChange; }, [onRangeChange]);

  // Notify range changes
  useEffect(() => {
    const cb = rangeCbRef.current;
    if (!cb) return;
    let from: Date, to: Date;
    if (view === 'month') ({ from, to } = monthRange(activeDate));
    else if (view === 'week') ({ from, to } = weekRange(activeDate));
    else if (view === 'day') ({ from, to } = dayRange(activeDate));
    else ({ from, to } = weekRange(activeDate));
    cb(from, to, view);
  }, [activeDate, view]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key.toLowerCase() === 't') goToday();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeDate, view]);

  const title = useMemo(() => {
    if (view === 'month') return activeDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const s = startOfWeek(activeDate);
      const e = new Date(s.getTime() + 6 * 24 * 60 * 60 * 1000);
      return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    }
    if (view === 'day') return activeDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    return 'Agenda';
  }, [activeDate, view]);

  const goToday = () => {
    setActiveDate(new Date());
  };
  const goPrev = () => {
    const d = new Date(activeDate);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else if (view === 'week') d.setDate(d.getDate() - 7);
    else if (view === 'day') d.setDate(d.getDate() - 1);
    else if (view === 'agenda') d.setDate(d.getDate() - 7);
    setActiveDate(d);
  };
  const goNext = () => {
    const d = new Date(activeDate);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else if (view === 'week') d.setDate(d.getDate() + 7);
    else if (view === 'day') d.setDate(d.getDate() + 1);
    else if (view === 'agenda') d.setDate(d.getDate() + 7);
    setActiveDate(d);
  };

  return (
    <div className={cn("w-full space-y-3", className)}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} aria-label="Previous">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goNext} aria-label="Next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="ml-2 text-lg font-semibold">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="Select date"
            value={activeDate.toISOString().slice(0, 10)}
            onChange={(e) => {
              const v = e.target.value;
              if (v) {
                const newDate = new Date(v + 'T00:00:00');
                // Validate date
                if (!isNaN(newDate.getTime())) {
                  setActiveDate(newDate);
                }
              }
            }}
          />
          <Select
            aria-label="Select view"
            value={view}
            onChange={(e) => setView(e.target.value as CalendarViewMode)}
          >
            <option value="month">Month</option>
            <option value="week">Week</option>
            <option value="day">Day</option>
            {/* Agenda view is intended for Upcoming lists; omitted from main calendar switcher */}
          </Select>
        </div>
      </div>

      {/* Body */}
      {view === 'month' && <MonthView month={activeDate} events={events} onEventClick={onEventClick} />}
      {view === 'week' && <WeekView weekStart={activeDate} events={events} onEventClick={onEventClick} />}
      {view === 'day' && <DayView day={activeDate} events={events} onEventClick={onEventClick} />}
      {view === 'agenda' && (() => {
        const { from, to } = weekRange(activeDate);
        return (
          <AgendaView
            from={from}
            to={to}
            events={events}
            onEventClick={onEventClick}
          />
        );
      })()}
    </div>
  );
};


